import { useCallback, useState } from 'react';
import { CheckCircle2, ChevronDown, ChevronUp, ClipboardList, FolderOpen, ArrowRight, ArrowLeft } from 'lucide-react';
import ResearchSelector from '../components/research/ResearchSelector';
import CompanyForm from '../components/research/CompanyForm';
import LocationCapture from '../components/research/LocationCapture';
import DynamicQuestionRenderer from '../components/research/DynamicQuestionRenderer';
import CommercialSection from '../components/research/CommercialSection';
import {
  CommercialData,
  CompanyData,
  FieldResearch,
  LocationData,
  ResearchForm,
  ResearchResponse,
} from '../types/fieldResearch';
import { getResearches, getResponses, saveResponses } from '../utils/storage';
import formsData from '../data/field-research-forms.json';
import AntaresLogo from '../components/AntaresLogo';

const EMPTY_COMPANY: CompanyData = {
  name: '',
  contactName: '',
  phone: '',
  instagram: '',
  cep: '',
  address: '',
  number: '',
  complement: '',
  district: '',
  city: '',
  state: '',
  usesSystem: '',
  systemName: '',
  systemPrice: '',
  systemPricingType: '',
};
const EMPTY_LOCATION: LocationData = { lat: null, lng: null };
const EMPTY_COMMERCIAL: CommercialData = {
  interestLevel: 'medio',
  visitStatus: 'visitado',
  nextStep: '',
};

// ── Helpers de arquivo / persistência ────────────────────────────────
async function adicionarRegistro(
  current: ResearchResponse[],
  item: ResearchResponse,
): Promise<ResearchResponse[]> {
  const updated = [...current, item];
  saveResponses(updated);
  return updated;
}

async function atualizarRegistro(
  current: ResearchResponse[],
  item: ResearchResponse,
): Promise<ResearchResponse[]> {
  const updated = current.map((r) => (r.id === item.id ? item : r));
  saveResponses(updated);
  return updated;
}

async function removerRegistro(
  current: ResearchResponse[],
  id: string,
): Promise<ResearchResponse[]> {
  const updated = current.filter((r) => r.id !== id);
  saveResponses(updated);
  return updated;
}

function selecionarArquivo(): Promise<ResearchResponse[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return resolve([]);
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const data = JSON.parse(ev.target?.result as string) as ResearchResponse[];
          resolve(Array.isArray(data) ? data : []);
        } catch {
          resolve([]);
        }
      };
      reader.readAsText(file);
    };
    input.click();
  });
}

function exportarJson(data: ResearchResponse[]): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `pesquisa-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Badge interesse ──────────────────────────────────────────────────
function InterestBadge({ level }: { level: CommercialData['interestLevel'] }) {
  const map = {
    alto: 'bg-green-500/20 text-green-300 border-green-500/30',
    medio: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
    baixo: 'bg-red-500/20 text-red-300 border-red-500/30',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${map[level]}`}>
      {level}
    </span>
  );
}

// ── Página principal ─────────────────────────────────────────────────
export default function FieldResearchPage() {
  const [researches] = useState<FieldResearch[]>(() =>
    getResearches().filter((r) => r.status === 'active'),
  );
  const forms = formsData as ResearchForm[];

  const [selectedResearch, setSelectedResearch] = useState<FieldResearch | null>(null);
  const [company, setCompany] = useState<CompanyData>(EMPTY_COMPANY);
  const [location, setLocation] = useState<LocationData>(EMPTY_LOCATION);
  const [answers, setAnswers] = useState<Record<string, string | string[] | number>>({});
  const [commercial, setCommercial] = useState<CommercialData>(EMPTY_COMMERCIAL);

  const [responses, setResponses] = useState<ResearchResponse[]>(() => getResponses());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState(false);
  const [fileLoaded, setFileLoaded] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [step, setStep] = useState(1);

  const currentForm = selectedResearch
    ? forms.find((f) => f.researchId === selectedResearch.id)
    : null;

  // ── Reset formulário ─────────────────────────────────────────────
  const resetForm = () => {
    setCompany(EMPTY_COMPANY);
    setLocation(EMPTY_LOCATION);
    setAnswers({});
    setCommercial(EMPTY_COMMERCIAL);
    setEditingId(null);
    setStep(1);
  };

  // ── Selecionar pesquisa ──────────────────────────────────────────
  const handleSelect = (r: FieldResearch) => {
    setSelectedResearch(r);
    resetForm();
    setFormOpen(true);
    setStep(1);
    setTimeout(() => window.scrollTo({ top: 200, behavior: 'smooth' }), 100);
  };

  // ── Responder pergunta ───────────────────────────────────────────
  const handleAnswer = useCallback(
    (id: string, value: string | string[] | number) =>
      setAnswers((prev) => ({ ...prev, [id]: value })),
    [],
  );

  // ── Salvar ───────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!selectedResearch) return;
    setSaving(true);

    const response: ResearchResponse = {
      id: editingId ?? `resp-${Date.now()}`,
      researchId: selectedResearch.id,
      createdAt: editingId
        ? (responses.find((r) => r.id === editingId)?.createdAt ?? new Date().toISOString())
        : new Date().toISOString(),
      company,
      location,
      answers,
      commercial,
    };

    const updated = editingId
      ? await atualizarRegistro(responses, response)
      : await adicionarRegistro(responses, response);

    setResponses(updated);
    setSaving(false);
    setSavedMsg(true);
    setTimeout(() => setSavedMsg(false), 2500);
    resetForm();
    setFormOpen(false);
  };

  // ── Editar ───────────────────────────────────────────────────────
  const handleEdit = (resp: ResearchResponse) => {
    const research = researches.find((r) => r.id === resp.researchId);
    if (research) setSelectedResearch(research);
    setCompany(resp.company);
    setLocation(resp.location);
    setAnswers(resp.answers);
    setCommercial(resp.commercial);
    setEditingId(resp.id);
    setFormOpen(true);
    setStep(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Excluir ──────────────────────────────────────────────────────
  const handleDelete = async (id: string) => {
    if (!window.confirm('Excluir este registro?')) return;
    const updated = await removerRegistro(responses, id);
    setResponses(updated);
    if (editingId === id) resetForm();
  };

  // ── Carregar arquivo ─────────────────────────────────────────────
  const handleLoadFile = async () => {
    const data = await selecionarArquivo();
    setResponses(data);
    setFileLoaded(true);
  };

  const researchLabel = (id: string) =>
    researches.find((r) => r.id === id)?.name ?? id;

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#0f0c29] text-white">
      {/* Hero Antares */}
      <header className="relative overflow-hidden bg-gradient-to-b from-[#0a0820] to-[#0f0c29] border-b border-white/10">
        {/* orbs decorativos */}
        <div className="absolute -top-16 -left-16 h-48 w-48 rounded-full blur-[80px] opacity-30" style={{ background: 'radial-gradient(circle,#7F5AF0,transparent 60%)' }} />
        <div className="absolute top-0 right-0 h-40 w-40 rounded-full blur-[80px] opacity-20" style={{ background: 'radial-gradient(circle,#2CB67D,transparent 60%)' }} />

        <div className="relative max-w-xl mx-auto px-4 pt-8 pb-6 flex flex-col items-center text-center gap-3">
          <div className="flex items-center gap-3">
            <AntaresLogo className="h-10 w-10 drop-shadow-lg" />
            <div className="text-left">
              <p className="text-xs font-semibold text-white/40 uppercase tracking-[0.15em]">Antares Tecnologia GTA</p>
              <h1 className="text-lg font-bold text-white leading-tight">Pesquisa de Campo</h1>
            </div>
          </div>
          <p className="text-xs text-white/35 max-w-xs">Coleta inteligente de dados em campo · offline-first · export JSON</p>
          {responses.length > 0 && (
            <span className="px-3 py-1 rounded-full bg-purple-500/20 border border-purple-500/30 text-purple-300 text-xs font-semibold">
              {responses.length} registro{responses.length !== 1 ? 's' : ''} salvos
            </span>
          )}
        </div>
      </header>

      <div className="max-w-xl mx-auto px-4 py-5 space-y-5 pb-20">

        {/* Feedback salvo */}
        {savedMsg && (
          <div className="flex items-center gap-2 bg-green-600/20 border border-green-500/30 text-green-300 px-4 py-3 rounded-xl text-sm animate-pulse">
            <CheckCircle2 size={15} />
            Registro salvo com sucesso!
          </div>
        )}

        {/* Seleção de pesquisa — compacto */}
        <section className="bg-white/5 border border-white/10 rounded-2xl p-4">
          <p className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-3">
            Selecione a pesquisa
          </p>
          <ResearchSelector
            researches={researches}
            selected={selectedResearch}
            onSelect={handleSelect}
          />
        </section>

        {/* Formulário em steps */}
        {selectedResearch && (
          <section className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
            {/* Header colapsável */}
            <button
              type="button"
              className="w-full flex items-center justify-between px-5 py-4 text-left"
              onClick={() => setFormOpen((v) => !v)}
            >
              <div>
                <p className="text-sm font-bold text-white">
                  {editingId ? '✏️ Editando registro' : '➕ Novo registro'}
                </p>
                <p className="text-xs text-white/40 mt-0.5">{selectedResearch.name}</p>
              </div>
              {formOpen ? (
                <ChevronUp size={18} className="text-white/40 shrink-0" />
              ) : (
                <ChevronDown size={18} className="text-white/40 shrink-0" />
              )}
            </button>

            {formOpen && (() => {
              const totalSteps = currentForm ? 4 : 3;
              const stepLabels = currentForm
                ? ['Estabelecimento', 'Localização', 'Perguntas', 'Comercial']
                : ['Estabelecimento', 'Localização', 'Comercial'];

              return (
                <div className="border-t border-white/10">
                  {/* Progress bar */}
                  <div className="px-5 pt-4 pb-3">
                    <div className="flex items-center gap-1.5 mb-2">
                      {stepLabels.map((label, i) => (
                        <div key={label} className="flex-1 flex flex-col items-center gap-1">
                          <div
                            className={`h-1.5 w-full rounded-full transition-all ${
                              i + 1 < step
                                ? 'bg-green-500'
                                : i + 1 === step
                                ? 'bg-purple-500'
                                : 'bg-white/10'
                            }`}
                          />
                          <span className={`text-[10px] font-medium transition-colors ${i + 1 === step ? 'text-purple-300' : i + 1 < step ? 'text-green-400' : 'text-white/25'}`}>
                            {i + 1 < step ? '✓' : label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Conteúdo do step */}
                  <div className="px-5 pb-2 space-y-4">
                    {/* Step 1 — Estabelecimento */}
                    {step === 1 && (
                      <>
                        <LocationCapture location={location} onCapture={setLocation} />
                        <CompanyForm data={company} onChange={setCompany} />
                      </>
                    )}

                    {/* Step 2 — Localização (CEP + endereço opcionais) */}
                    {step === 2 && (
                      <div className="space-y-3">
                        <div className="rounded-xl bg-white/5 border border-white/10 p-4">
                          <p className="text-sm font-bold text-white mb-1">📍 Localização</p>
                          <p className="text-xs text-white/40 mb-4">GPS já capturado. CEP e endereço são opcionais.</p>
                          {location.lat !== null && (
                            <p className="text-xs text-green-400 mb-4">✓ GPS: {location.lat.toFixed(5)}, {location.lng?.toFixed(5)}</p>
                          )}
                          <CompanyForm data={company} onChange={setCompany} addressOnly />
                        </div>
                      </div>
                    )}

                    {/* Step 3 — Perguntas dinâmicas (só se tiver formulário) */}
                    {step === 3 && currentForm && (
                      <div className="rounded-xl bg-white/5 border border-white/10 p-4">
                        <DynamicQuestionRenderer
                          questions={currentForm.questions}
                          answers={answers}
                          onChange={handleAnswer}
                        />
                      </div>
                    )}

                    {/* Step 3 sem perguntas OU Step 4 com perguntas — Comercial */}
                    {((!currentForm && step === 3) || (currentForm && step === 4)) && (
                      <div className="rounded-xl bg-white/5 border border-white/10 p-4">
                        <CommercialSection data={commercial} onChange={setCommercial} />
                      </div>
                    )}
                  </div>

                  {/* Navegação */}
                  <div className="px-5 py-4 flex gap-3">
                    {step > 1 && (
                      <button
                        type="button"
                        onClick={() => setStep((s) => s - 1)}
                        className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border border-white/15 bg-white/5 text-white text-sm font-medium hover:bg-white/10 transition active:scale-95"
                      >
                        <ArrowLeft size={15} />
                        Voltar
                      </button>
                    )}

                    {step < totalSteps ? (
                      <button
                        type="button"
                        disabled={step === 1 && !company.name.trim()}
                        onClick={() => setStep((s) => s + 1)}
                        className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-brand-primary to-brand-secondary text-white text-sm font-semibold hover:brightness-110 transition active:scale-95 disabled:opacity-40 disabled:pointer-events-none shadow-[0_0_20px_rgba(233,30,140,0.25)]"
                      >
                        Próximo
                        <ArrowRight size={15} />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                        className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-green-600/20 border border-green-500/40 text-green-300 text-sm font-semibold hover:bg-green-600/30 transition active:scale-95 disabled:opacity-40"
                      >
                        {saving ? '💾 Salvando...' : '✅ Salvar registro'}
                      </button>
                    )}
                  </div>

                  {/* Cancelar */}
                  {editingId && (
                    <div className="px-5 pb-4">
                      <button
                        type="button"
                        onClick={resetForm}
                        className="w-full py-2.5 rounded-xl text-xs text-white/35 hover:text-white/55 transition"
                      >
                        Cancelar edição
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}
          </section>
        )}

        {/* Lista de registros */}
        <section className="space-y-3">
          <div className="flex items-center justify-between px-1">
            <h2 className="text-sm font-bold text-white">
              Registros Salvos
            </h2>
            <button
              type="button"
              onClick={handleLoadFile}
              className="flex items-center gap-1.5 text-xs text-purple-300 hover:text-purple-200 transition"
            >
              <FolderOpen size={13} />
              {fileLoaded ? 'Recarregar arquivo' : 'Abrir arquivo JSON'}
            </button>
          </div>

          {responses.length === 0 ? (
            <div className="bg-white/5 border border-dashed border-white/10 rounded-2xl p-10 text-center">
              <ClipboardList size={30} className="mx-auto text-white/15 mb-3" />
              <p className="text-white/40 text-sm">Nenhum registro ainda.</p>
              <p className="text-white/25 text-xs mt-1">
                Salve um registro ou abra um arquivo JSON.
              </p>
            </div>
          ) : (
            <>
              {responses.map((r) => (
                <div
                  key={r.id}
                  className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden"
                >
                  {/* Cabeçalho do card */}
                  <button
                    type="button"
                    className="w-full text-left px-5 py-4 flex items-center justify-between gap-3"
                    onClick={() =>
                      setExpandedId(expandedId === r.id ? null : r.id)
                    }
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white truncate">
                        {r.company.name || 'Sem nome'}
                      </p>
                      <p className="text-xs text-white/40 mt-0.5 truncate">
                        {researchLabel(r.researchId)} ·{' '}
                        {r.company.city || '—'} ·{' '}
                        {new Date(r.createdAt).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <InterestBadge level={r.commercial.interestLevel} />
                      {expandedId === r.id ? (
                        <ChevronUp size={15} className="text-white/30" />
                      ) : (
                        <ChevronDown size={15} className="text-white/30" />
                      )}
                    </div>
                  </button>

                  {/* Detalhe expandido */}
                  {expandedId === r.id && (
                    <div className="px-5 pb-4 space-y-3 border-t border-white/10">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 pt-3 text-xs text-white/55">
                        {r.company.phone && (
                          <p>📞 {r.company.phone}</p>
                        )}
                        {r.company.instagram && (
                          <p>📷 {r.company.instagram}</p>
                        )}
                        {r.company.contactName && (
                          <p>👤 {r.company.contactName}</p>
                        )}
                        {r.commercial.visitStatus && (
                          <p>🏷️ {r.commercial.visitStatus.replace('_', ' ')}</p>
                        )}
                        {r.company.address && (
                          <p className="col-span-2">
                            📍 {r.company.address}
                            {r.company.district ? `, ${r.company.district}` : ''}
                          </p>
                        )}
                        {r.location.lat !== null && (
                          <p className="col-span-2">
                            🗺️ {r.location.lat.toFixed(5)},{' '}
                            {r.location.lng?.toFixed(5)}
                          </p>
                        )}
                        {r.commercial.nextStep && (
                          <p className="col-span-2 text-white/40 italic">
                            💬 {r.commercial.nextStep}
                          </p>
                        )}
                      </div>

                      <div className="flex gap-2 pt-1">
                        <button
                          type="button"
                          onClick={() => handleEdit(r)}
                          className="flex-1 py-2.5 rounded-xl bg-blue-600/15 border border-blue-500/30 text-blue-300 text-xs font-medium hover:bg-blue-600/25 transition"
                        >
                          ✏️ Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(r.id)}
                          className="flex-1 py-2.5 rounded-xl bg-red-600/15 border border-red-500/30 text-red-300 text-xs font-medium hover:bg-red-600/25 transition"
                        >
                          🗑️ Excluir
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={() => exportarJson(responses)}
                className="w-full py-3.5 rounded-xl bg-green-600/15 border border-green-500/30 text-green-300 text-sm font-medium hover:bg-green-600/25 transition"
              >
                ↓ Exportar todos os registros em JSON
              </button>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
